import { minBy } from "lodash-es";

import type {
  FiDataPointBinding,
  FiDataPointLimits,
} from "@mapmycustomers/shared/types/entity/fieldInsight";

import type { DatasetConfig } from "../types";

export type DataPointKind = "charts" | "customFields";

export interface DataPointCounts {
  charts: number;
  customFields: number;
}

export interface DataPointBudget {
  // Every message and every disabled option keys off this, so no caller encodes the sharing rule.
  binding: FiDataPointBinding;
  // Both counts ride along: under the shared allowance the admin needs to see where it went, and
  // that is as often custom fields as charts.
  chartsUsed: number;
  customFieldsUsed: number;
  // Absent only when nothing caps this kind at all.
  limit?: number;
  // Negative when saved settings sit over the cap.
  remaining: number;
}

export const getDatasetDataPointCounts = (dataset: DatasetConfig): DataPointCounts => ({
  charts: dataset.chartData?.length ?? 0,
  customFields: dataset.customFields?.length ?? 0,
});

/**
 * Mirrors exceededDataPointLimit / customFieldBudget in mmc-server's
 * services/constants/field-insights-limits.ts: a per-kind cap is checked before the shared total,
 * so the tightest constraint binds and ties go to the per-kind one — the order the API rejects in.
 */
const getDataPointBudget = (
  counts: DataPointCounts,
  limits: FiDataPointLimits,
  kind: DataPointKind
): DataPointBudget => {
  const ownLimit = kind === "charts" ? limits.maxCharts : limits.maxCustomFields;

  const constraints: DataPointBudget[] = [];
  if (ownLimit !== undefined) {
    constraints.push({
      binding: kind,
      chartsUsed: counts.charts,
      customFieldsUsed: counts.customFields,
      limit: ownLimit,
      remaining: ownLimit - counts[kind],
    });
  }
  if (limits.maxTotal !== undefined) {
    constraints.push({
      binding: "total",
      chartsUsed: counts.charts,
      customFieldsUsed: counts.customFields,
      limit: limits.maxTotal,
      remaining: limits.maxTotal - counts.charts - counts.customFields,
    });
  }

  // minBy keeps the first of equal remainders, so a tie still goes to the per-kind cap pushed
  // above. No constraint at all means no cap of either kind, so nothing is ever refused.
  return (
    minBy(constraints, "remaining") ?? {
      binding: "total",
      chartsUsed: counts.charts,
      customFieldsUsed: counts.customFields,
      remaining: Number.POSITIVE_INFINITY,
    }
  );
};

export default getDataPointBudget;
